import { ApiResponse } from 'apisauce';
import { CalendarEvent, DeletePlannedTaskResponse, PlanTasksRequest, ViewPlannedTask } from 'shovel-lib/types';
import { network } from './network';
import { sameAction } from './activityLogApi';

export type TaskPlanningInfo = {
  id: number;
  title: string;
  dueDate: Date;
  daysToStartAhead: number;
  timeNeed: number;
  timePlanned: number;
  // Minutes of blocks marked used.
  timeSpent: number;
  priority: number;
  courseId: number;
  courseName: string;
  courseColor: string;
  // Pages still to read, for a reading task. Zero for anything measured in time.
  pagesLeft: number | null;
  // For a review session, the reading it reviews and which review it is (1 or 2); null for anything else.
  reviewOfTaskId?: number | null;
  reviewNumber?: number | null;
};

/** A review session these dates should have and doesn't yet: unsaved, so it has no id. */
export type MissingReview = Omit<TaskPlanningInfo, 'id' | 'reviewOfTaskId' | 'reviewNumber'> & {
  id: null;
  reviewOfTaskId: number;
  reviewNumber: number;
};

export type UnplanScope = 'AUTO_PLANNED' | 'ALL';

export type UnplanRequest = {
  from: Date;
  to: Date;
  scope: UnplanScope;
};

/** The tasks Auto-plan just planned time for, whose missed sessions come off the calendar. */
export type RemoveMissedRequest = {
  taskIds: number[];
};

export type UnplanResponse = {
  deletedPlannedTaskIds: number[];
  tasks: DeletePlannedTaskResponse[];
  // The removed sessions as they were, for an undo of a Replan to put back. Only the server reads them.
  removedSessions?: unknown[];
  // From an undo: the sessions it put back, as plan-bulk returns new ones.
  restoredSessions?: CalendarEvent[];
  // From an undo with places: the sessions it moved back where they were.
  movedSessions?: CalendarEvent[];
};

/** One block to mark as used. `pages` is only read for a reading, which credits pages rather than minutes. */
export type MarkUsedBlock = {
  plannedTaskId: number;
  pages?: number;
  minutes?: number;
  /** Left out means mark it done; false puts it back and returns the time to the task. */
  used?: boolean;
};

export type MarkUsedRequest = {
  blocks: MarkUsedBlock[];
};

export type MarkUsedResponse = {
  plannedTasks: ViewPlannedTask[];
  tasks: DeletePlannedTaskResponse[];
  // Blocks left alone: already used, gone, running the timer, or a reading with no page count.
  skippedPlannedTaskIds: number[];
};

export interface PlannerApi {
  getActiveTasksInRange: (semesterId: number, from: string, to: string) => Promise<ApiResponse<TaskPlanningInfo[]>>;
  unplan: (semesterId: number, request: UnplanRequest, actionId?: string) => Promise<ApiResponse<UnplanResponse>>;
  planBulk: (semesterId: number, blocks: PlanTasksRequest, actionId?: string) => Promise<ApiResponse<CalendarEvent[]>>;
  removeMissed: (
    semesterId: number,
    request: RemoveMissedRequest,
    actionId?: string
  ) => Promise<ApiResponse<UnplanResponse>>;
  markUsed: (semesterId: number, request: MarkUsedRequest) => Promise<ApiResponse<MarkUsedResponse>>;
  getMissingReviews: (semesterId: number, from: string, to: string) => Promise<ApiResponse<MissingReview[]>>;
  createReviews: (
    semesterId: number,
    from: string,
    to: string,
    actionId?: string
  ) => Promise<ApiResponse<TaskPlanningInfo[]>>;
  getOverdueTasks: (semesterId: number, from: string) => Promise<ApiResponse<TaskPlanningInfo[]>>;
  movePlannedTasks: (semesterId: number, moves: PlannedTaskMove[]) => Promise<ApiResponse<CalendarEvent[]>>;
  rearrangeSessions: (semesterId: number, request: RearrangeRequest) => Promise<ApiResponse<RearrangeResponse>>;
}

/** Part of a session split to fit: a new session of the task of `fromId`, which is moved in the same request. */
export type PlannedTaskPiece = { fromId: number; start: string; end: string };

/** The timer bumping the day down (POST /planner/rearrange), all or none. */
export type RearrangeRequest = { moves: PlannedTaskMove[]; pieces?: PlannedTaskPiece[]; removeIds?: number[] };

export type RearrangeResponse = {
  moved: CalendarEvent[];
  created: CalendarEvent[];
  deletedPlannedTaskIds: number[];
  // The removed sessions as they were, for its undo to put back. Only the server reads them.
  removedSessions: unknown[];
  tasks: DeletePlannedTaskResponse[];
};

/** A session's new place, for moving several at once, all or none (POST /planner/move-bulk; timer spec step 5). */
export type PlannedTaskMove = { id: number; start: string; end: string };
const basePath = (semesterId: number) => `/semesters/${semesterId}/planner`;

const getActiveTasksInRange = (semesterId: number, from: string, to: string) =>
  network.get<TaskPlanningInfo[]>(`${basePath(semesterId)}/tasks-in-range`, { from, to });

// `actionId` on these: a later step of a change that already has one (activity log, api/activityLogApi.ts).
const unplan = (semesterId: number, request: UnplanRequest, actionId?: string) =>
  network.post<UnplanResponse>(`${basePath(semesterId)}/unplan`, request, { headers: sameAction(actionId) });

// shovel-lib's createPlannedTasks, able to say it belongs to the task just made for it.
const planBulk = (semesterId: number, blocks: PlanTasksRequest, actionId?: string) =>
  network.post<CalendarEvent[]>(`${basePath(semesterId)}/plan-bulk`, blocks, { headers: sameAction(actionId) });

// After a plan: their time is in it now, so left on the calendar their Log time would credit the task twice. `actionId`:
// the plan's, so its Undo covers these too.
const removeMissed = (semesterId: number, request: RemoveMissedRequest, actionId?: string) =>
  network.post<UnplanResponse>(`${basePath(semesterId)}/remove-missed`, request, { headers: sameAction(actionId) });

// One request for the whole set: a mark-as-used per block would re-render the calendar and refetch the pile
// each time, the same cost unplanning a week was batched to avoid.
const markUsed = (semesterId: number, request: MarkUsedRequest) =>
  network.post<MarkUsedResponse>(`${basePath(semesterId)}/mark-used`, request);

// Review sessions (Auto-plan's "Add review sessions after reading tasks"): the ones these dates should have and don't,
// unsaved for the preview, and made for real just before a plan.
const getMissingReviews = (semesterId: number, from: string, to: string) =>
  network.get<MissingReview[]>(`${basePath(semesterId)}/reviews`, { from, to });

const createReviews = (semesterId: number, from: string, to: string, actionId?: string) =>
  network.post<TaskPlanningInfo[]>(`${basePath(semesterId)}/reviews?from=${from}&to=${to}`, undefined, {
    headers: sameAction(actionId)
  });

/** Tasks that passed their due date since `from` with time still needed, soonest due first. */
const getOverdueTasks = (semesterId: number, from: string) =>
  network.get<TaskPlanningInfo[]>(`${basePath(semesterId)}/overdue`, { from });

const movePlannedTasks = (semesterId: number, moves: PlannedTaskMove[]) =>
  network.post<CalendarEvent[]>(`${basePath(semesterId)}/move-bulk`, { moves });

const rearrangeSessions = (semesterId: number, request: RearrangeRequest) =>
  network.post<RearrangeResponse>(`${basePath(semesterId)}/rearrange`, request);

export const plannerApi: PlannerApi = {
  movePlannedTasks,
  rearrangeSessions,
  getActiveTasksInRange,
  unplan,
  planBulk,
  removeMissed,
  markUsed,
  getMissingReviews,
  createReviews,
  getOverdueTasks
};
